// src/services/fiscalService.js
// *******************************************************************************
// FISCAL SERVICE
// *******************************************************************************
// Centralized service for fiscal operations including tax calculations,
// SEFAZ integration, certificate management, and invoice emission.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import axiosService from './axiosService';
import { API } from 'constants/Api';
import appService from './appService';
import translateX from 'utils/translateX';
import { roundCurrency, percentageOf } from './financialService';

// *******************************************************************************
// SEFAZ CONSTANTS
// *******************************************************************************

/**
 * Payment types according to SEFAZ (tPag)
 * Used in NFe/NFCe payment methods
 */
const PAYMENT_TYPES = {
  MONEY: { code: '01', labelKey: 'payment_money' },
  CHECK: { code: '02', labelKey: 'payment_check' },
  CREDIT_CARD: { code: '03', labelKey: 'payment_credit_card' },
  DEBIT_CARD: { code: '04', labelKey: 'payment_debit_card' },
  STORE_CREDIT: { code: '05', labelKey: 'payment_store_credit' },
  FOOD_VOUCHER: { code: '10', labelKey: 'payment_food_voucher' },
  MEAL_VOUCHER: { code: '11', labelKey: 'payment_meal_voucher' },
  GIFT_VOUCHER: { code: '12', labelKey: 'payment_gift_voucher' },
  FUEL_VOUCHER: { code: '13', labelKey: 'payment_fuel_voucher' },
  DUPLICATE: { code: '14', labelKey: 'payment_duplicate' },
  BANK_SLIP: { code: '15', labelKey: 'payment_bank_slip' },
  BANK_DEPOSIT: { code: '16', labelKey: 'payment_bank_deposit' },
  PIX: { code: '17', labelKey: 'payment_pix' },
  BANK_TRANSFER: { code: '18', labelKey: 'payment_bank_transfer' },
  LOYALTY_PROGRAM: { code: '19', labelKey: 'payment_loyalty_program' },
  NO_PAYMENT: { code: '90', labelKey: 'payment_no_payment' },
  OTHER: { code: '99', labelKey: 'payment_other' },
};

/**
 * Payment indicator (indPag)
 */
const PAYMENT_INDICATOR = {
  CASH: 0, // Cash payment
  INSTALLMENT: 1, // Pagamento a prazo
  OTHER: 2, // Outros
};

/**
 * Document types
 */
const DOCUMENT_TYPES = {
  NFE: 'nfe',
  NFCE: 'nfce',
  NFSE: 'nfse',
};

/**
 * Document status
 * Aligned with backend: src/modules/domain/invoices/constants/invoiceStatus.js
 */
const DOCUMENT_STATUS = {
  // Pre-emission states
  DRAFT: 'draft', // Draft - editable document, not submitted

  // Processing states
  PROCESSING: 'processing', // Em processamento - enviado para SEFAZ, aguardando resposta

  // Final success states
  AUTHORIZED: 'authorized', // Authorized by SEFAZ - valid fiscal document
  CORRECTED: 'corrected', // Has correction letter (CCe)

  // Final error states
  REJECTED: 'rejected', // Rejeitado pela SEFAZ - erro nos dados, pode corrigir e reenviar
  DENIED: 'denied', // Denied by SEFAZ - issuer issues, cannot resubmit
  ERROR: 'error', // Technical error - timeout, communication failure, can reprocess

  // Post-emission states
  CANCELED: 'canceled', // Canceled after authorization (cancellation event)
  VOIDED: 'voided', // Voided - numbering voided at SEFAZ
};

/**
 * Invoice purpose (finNFe)
 * SEFAZ codes: 1=Normal, 2=Complementar, 3=Ajuste, 4=Devolução
 */
const INVOICE_PURPOSE = {
  NORMAL: 'normal',
  COMPLEMENTARY: 'complementary',
  ADJUSTMENT: 'adjustment',
  RETURN: 'return',
};

/**
 * Invoice purpose SEFAZ codes mapping (finNFe)
 * Used when sending to SEFAZ
 */
const INVOICE_PURPOSE_CODES = {
  normal: 1,
  complementary: 2,
  adjustment: 3,
  return: 4,
};

/**
 * NFe model codes
 */
const NFE_MODEL = {
  NFE: 55, // NF-e (Electronic Invoice)
  NFCE: 65, // NFC-e (Electronic Consumer Invoice)
};

/**
 * Recipient IE indicator (indIEDest)
 * SEFAZ codes: 1=Contribuinte, 2=Isento, 9=Não contribuinte
 */
const RECIPIENT_IE_INDICATOR = {
  CONTRIBUTOR: 1, // Contribuinte ICMS (has IE)
  EXEMPT: 2, // Isento de ICMS (IE exempt)
  NON_CONTRIBUTOR: 9, // Non-contributor (no IE)
};

/**
 * NFe emission type (tpEmis)
 * SEFAZ codes: 1=Normal, 2=Contingência FS-IA, 3=SCAN, 4=DPEC, 5=FS-DA, 6=SVC-AN, 7=SVC-RS, 9=Offline NFC-e
 */
const NFE_EMISSION_TYPE = {
  NORMAL: 1,
  CONTINGENCY_FS_IA: 2,
  SCAN: 3,
  DPEC: 4,
  CONTINGENCY_FS_DA: 5,
  SVC_AN: 6,
  SVC_RS: 7,
  OFFLINE_NFCE: 9,
};

/**
 * Operation type (tpNF)
 * SEFAZ codes: 0=Entrada, 1=Saída
 */
const OPERATION_TYPE = {
  ENTRY: 'entry',
  EXIT: 'exit',
};

/**
 * Consumer presence (indPres)
 * SEFAZ codes: 0=N/A, 1=Presencial, 2=Internet, 3=Telemarketing, 4=Entrega, 5=Fora, 9=Outros
 */
const CONSUMER_PRESENCE = {
  NOT_APPLICABLE: 'notApplicable',
  IN_PERSON: 'inPerson',
  INTERNET: 'internet',
  TELEMARKETING: 'telemarketing',
  DELIVERY_NFCE: 'delivery',
  IN_PERSON_OUTSIDE: 'outside',
  OTHER: 'other',
};

/**
 * Tax regime (CRT)
 */
const TAX_REGIME = {
  SIMPLES_NACIONAL: 1,
  SIMPLES_NACIONAL_EXCESS: 2,
  REGIME_NORMAL: 3,
  SIMPLES_NACIONAL_MEI: 4,
};

/**
 * CRT Tag configuration for display (label, color)
 * CRT1 = Simples Nacional
 * CRT2 = Simples Nacional – excesso de sublimite
 * CRT3 = Regime Normal (Lucro Presumido ou Lucro Real)
 * CRT4 = MEI (Simples Nacional - MEI)
 */
const CRT_TAG_CONFIG = {
  1: { label: 'CRT1', color: 'green', description: 'Simples Nacional' },
  2: { label: 'CRT2', color: 'orange', description: 'Simples Nacional - Excesso' },
  3: { label: 'CRT3', color: 'blue', description: 'Regime Normal' },
  4: { label: 'CRT4', color: 'cyan', description: 'MEI' },
};

/**
 * ICMS origin codes
 */
const ICMS_ORIGIN = {
  NATIONAL: 0,
  FOREIGN_DIRECT: 1,
  FOREIGN_ACQUIRED: 2,
  NATIONAL_40_70: 3,
  NATIONAL_PROCESS: 4,
  NATIONAL_UNDER_40: 5,
  FOREIGN_DIRECT_NO_SIMILAR: 6,
  FOREIGN_ACQUIRED_NO_SIMILAR: 7,
  NATIONAL_OVER_70: 8,
};

/**
 * Common ICMS CST codes (Regime Normal)
 * Full list including ST (Substituição Tributária) codes
 */
const ICMS_CST = {
  // Without ST (Tax Substitution)
  FULLY_TAXED: '00', // Tributada integralmente
  TAXED_WITH_CREDIT: '20', // With calculation base reduction
  EXEMPT: '40', // Isenta
  NOT_TAXED: '41', // Not taxed
  SUSPENDED: '50', // Suspended
  DEFERRED: '51', // Diferimento
  OTHER: '90', // Outros

  // With ST (Tax Substitution) - NFe only
  ST_TAXED: '10', // Taxed with ICMS collection via ST
  ST_EXEMPT: '30', // Exempt or not taxed with ICMS collection via ST
  ST_COLLECTED: '60', // ICMS previously collected via ST
  ST_REDUCED: '70', // With calculation base reduction and ICMS collection via ST
};

/**
 * ICMS CST codes that involve Substituição Tributária (ST)
 * These require additional calculations and fields
 */
const ICMS_CST_WITH_ST = ['10', '30', '60', '70'];

/**
 * Common ICMS CSOSN codes (Simples Nacional)
 */
const ICMS_CSOSN = {
  // Without ST (Tax Substitution)
  TAXED_WITH_CREDIT: '101', // Taxed with credit permission
  TAXED_NO_CREDIT: '102', // Taxed without credit permission
  EXEMPT_ICMS: '103', // ICMS exemption
  IMMUNE: '300', // Imune
  NOT_TAXED: '400', // Not taxed
  OTHER: '900', // Outros

  // With ST (Tax Substitution)
  TAXED_ST: '201', // Taxed by SN with ST and credit permission
  TAXED_ST_NO_CREDIT: '202', // Taxed by SN with ST without credit permission
  EXEMPT_ST: '203', // ICMS exemption in SN with ST
  ST_COLLECTED: '500', // ICMS cobrado anteriormente por ST
};

/**
 * ICMS CSOSN codes that involve Substituição Tributária (ST)
 * These require additional calculations and fields
 */
const ICMS_CSOSN_WITH_ST = ['201', '202', '203', '500'];

/**
 * Common PIS/COFINS CST codes
 */
const PIS_COFINS_CST = {
  CUMULATIVE: '01', // Taxable operation - calculation base (cumulative)
  NON_CUMULATIVE: '02', // Taxable operation - calculation base (non-cumulative)
  ZERO_RATE: '04', // Taxable operation - zero rate
  ST: '05', // Taxable operation - ST
  ZERO_RATE_ALT: '06', // Taxable operation - zero rate (outra)
  EXEMPT: '07', // Contribution-exempt operation
  WITHOUT_INCIDENCE: '08', // Operation without contribution incidence
  SUSPENDED: '09', // Operation with contribution suspension
  OTHER_EXIT: '49', // Other exit operations
  OTHER_ENTRY: '99', // Other operations
};

/**
 * CFOPs that allow zero-value items (bonification, gift, sample, promotional, etc.)
 * These CFOPs indicate non-commercial operations where value can be zero
 *
 * Within state (5xxx):
 * - 5910: Remessa em bonificação (gift/bonus)
 * - 5911: Remessa de amostra grátis (free sample)
 * - 5912: Remessa de mercadoria/bem para demonstração (demonstration)
 * - 5913: Retorno de mercadoria/bem recebido para demonstração
 * - 5914: Remessa de mercadoria/bem para exposição ou feira
 * - 5915: Remessa de mercadoria/bem para conserto ou reparo
 * - 5916: Retorno de mercadoria/bem recebido para conserto ou reparo
 * - 5917: Remessa de mercadoria em consignação
 * - 5918: Devolução de mercadoria recebida em consignação
 * - 5919: Devolução simbólica de mercadoria vendida ou utilizada
 * - 5920: Remessa de vasilhame ou sacaria
 * - 5921: Devolução de vasilhame ou sacaria
 * - 5922: Lançamento a título de simples faturamento
 * - 5923: Remessa de mercadoria por conta e ordem de terceiros
 * - 5924: Remessa para industrialização por conta e ordem
 * - 5925: Retorno de mercadoria industrializada por conta e ordem
 * - 5949: Outra saída de mercadoria não especificada (other exit)
 *
 * Interstate (6xxx) - equivalent operations for out-of-state
 */
const CFOP_ZERO_VALUE_ALLOWED = [
  // Within state (5xxx)
  '5910', // Bonus shipment
  '5911', // Free sample shipment
  '5912', // Demo shipment
  '5913', // Demo return
  '5914', // Exhibition/fair shipment
  '5915', // Remessa para conserto/reparo
  '5916', // Retorno de conserto/reparo
  '5917', // Consignment shipment
  '5918', // Consignment return
  '5919', // Symbolic return
  '5920', // Remessa de vasilhame
  '5921', // Container return
  '5922', // Simples faturamento
  '5923', // Por conta e ordem de terceiros
  '5924', // Industrialization on account and order
  '5925', // Industrialization return
  '5949', // Other unspecified exit

  // Interstate (6xxx)
  '6910', // Bonus shipment
  '6911', // Free sample shipment
  '6912', // Demo shipment
  '6913', // Demo return
  '6914', // Exhibition/fair shipment
  '6915', // Remessa para conserto/reparo
  '6916', // Retorno de conserto/reparo
  '6917', // Consignment shipment
  '6918', // Consignment return
  '6919', // Symbolic return
  '6920', // Remessa de vasilhame
  '6921', // Container return
  '6922', // Simples faturamento
  '6923', // Por conta e ordem de terceiros
  '6924', // Industrialization on account and order
  '6925', // Industrialization return
  '6949', // Other unspecified exit
];

/**
 * Freight modality (modFrete)
 * SEFAZ codes: 0=CIF, 1=FOB, 2=Terceiros, 3=Próprio/Emitente, 4=Próprio/Destinatário, 9=Sem frete
 */
const FREIGHT_MODALITY = {
  SENDER: 'sender', // Por conta do emitente (CIF)
  RECIPIENT: 'recipient', // Recipient account (FOB)
  THIRD_PARTY: 'thirdParty', // Por conta de terceiros
  OWN_SENDER: 'ownSender', // Own on issuer account
  OWN_RECIPIENT: 'ownRecipient', // Own on recipient account
  NO_FREIGHT: 'noFreight', // Sem frete
};

/**
 * Emission environment (tpAmb)
 * SEFAZ codes: 1=Produção, 2=Homologação
 */
const EMISSION_ENVIRONMENT = {
  PRODUCTION: 'production',
  HOMOLOGATION: 'homologation',
};

/**
 * Final consumer indicator (indFinal)
 * SEFAZ codes: 0=Não, 1=Sim
 */
const FINAL_CONSUMER = {
  NO: false,
  YES: true,
};

/**
 * Intermediation indicator (indIntermed)
 * SEFAZ codes: 0=Própria, 1=Marketplace
 */
const INTERMEDIATION = {
  OWN_OPERATION: 'own',
  VIA_INTERMEDIARY: 'intermediary',
};

/**
 * Invoice nature (natureza da operação)
 * Standardized values in lowercase English
 */
const INVOICE_NATURE = {
  SALE: 'sale',
  RETURN: 'return',
  TRANSFER: 'transfer',
  BONUS: 'bonus',
  DEMO: 'demo',
  SAMPLE: 'sample',
  CONSIGNMENT: 'consignment',
  OTHER: 'other',
};

// *******************************************************************************
// TAX CALCULATION FUNCTIONS
// *******************************************************************************

/**
 * Calculate ICMS for an invoice item
 */
function calculateIcms({
  baseValue,
  rate,
  reductionRate = 0,
  cst = null,
  csosn = null,
  origin = 0,
}) {
  const base = roundCurrency(baseValue || 0);
  const icmsRate = rate || 0;
  const reduction = reductionRate || 0;

  // Apply base reduction if any
  const reducedBase = reduction > 0 ? roundCurrency(base * (1 - reduction / 100)) : base;

  // Calculate ICMS value
  const value = roundCurrency(percentageOf(reducedBase, icmsRate));

  return {
    origin,
    cst,
    csosn,
    baseValue: reducedBase,
    rate: icmsRate,
    reductionRate: reduction,
    value,
  };
}

/**
 * Calculate PIS for an invoice item
 */
function calculatePis({ baseValue, rate, cst = '01' }) {
  const base = roundCurrency(baseValue || 0);
  const pisRate = rate || 0;
  const value = roundCurrency(percentageOf(base, pisRate));

  return {
    cst,
    baseValue: base,
    rate: pisRate,
    value,
  };
}

/**
 * Calculate COFINS for an invoice item
 */
function calculateCofins({ baseValue, rate, cst = '01' }) {
  const base = roundCurrency(baseValue || 0);
  const cofinsRate = rate || 0;
  const value = roundCurrency(percentageOf(base, cofinsRate));

  return {
    cst,
    baseValue: base,
    rate: cofinsRate,
    value,
  };
}

/**
 * Calculate IPI for an invoice item (for manufacturers)
 */
function calculateIpi({ baseValue, rate, cst = '50' }) {
  const base = roundCurrency(baseValue || 0);
  const ipiRate = rate || 0;
  const value = roundCurrency(percentageOf(base, ipiRate));

  return {
    cst,
    baseValue: base,
    rate: ipiRate,
    value,
  };
}

// *******************************************************************************
// ICMS-ST (TAX SUBSTITUTION) CALCULATIONS - NFe ONLY
// *******************************************************************************

/**
 * Calculate ICMS-ST base value using MVA (Margem de Valor Agregado)
 * Formula: Base ST = (Valor Produtos + Frete + Seguro + Despesas + IPI) × (1 + MVA%)
 *
 */
function calculateIcmsStBase({
  productValue,
  freightValue = 0,
  insuranceValue = 0,
  otherCharges = 0,
  ipiValue = 0,
  mvaRate,
}) {
  const baseWithCharges = roundCurrency(
    (productValue || 0) +
      (freightValue || 0) +
      (insuranceValue || 0) +
      (otherCharges || 0) +
      (ipiValue || 0)
  );

  // Apply MVA
  const mvaMultiplier = 1 + (mvaRate || 0) / 100;
  return roundCurrency(baseWithCharges * mvaMultiplier);
}

/**
 * Calculate ICMS-ST value
 * Formula: ICMS-ST = (Base ST × Alíquota Interna) - ICMS Próprio
 *
 */
function calculateIcmsSt({ stBaseValue, internalRate, ownIcmsValue, fcpRate = 0 }) {
  const stBase = roundCurrency(stBaseValue || 0);
  const internalAliquot = internalRate || 0;
  const ownIcms = ownIcmsValue || 0;

  // ICMS-ST = (ST Base x Internal Rate) - Own ICMS
  const stIcmsBeforeSubtraction = roundCurrency(percentageOf(stBase, internalAliquot));
  const stValue = roundCurrency(Math.max(0, stIcmsBeforeSubtraction - ownIcms));

  // FCP (Poverty Fund) - additional tax in some states
  const fcpValue = fcpRate > 0 ? roundCurrency(percentageOf(stBase, fcpRate)) : 0;

  return {
    stBaseValue: stBase,
    stRate: internalAliquot,
    stValue,
    fcpRate,
    fcpValue,
    // Total ST = ICMS-ST + FCP
    stTotalValue: roundCurrency(stValue + fcpValue),
  };
}

/**
 * Check if a CST/CSOSN code involves Substituição Tributária
 */
function isStCode(code, isSimples = false) {
  if (!code) return false;
  const codeStr = String(code);
  return isSimples ? ICMS_CSOSN_WITH_ST.includes(codeStr) : ICMS_CST_WITH_ST.includes(codeStr);
}

/**
 * Get default MVA rate by NCM/CEST code
 * This is a simplified implementation - in production, use a complete table
 * MVA varies by product (NCM), operation type, and origin/destination states
 *
 */
function getMvaRate({ ncm, cest, originUf, destinationUf } = {}) {
  // Default MVA rates by product category (simplified)
  // In production, this should be a complete lookup table from CONFAZ
  const DEFAULT_MVA_BY_CATEGORY = {
    // Beverages
    22: 140, // NCM 22.xx - Bebidas
    // Pharmaceuticals
    30: 33, // NCM 30.xx - Pharmaceuticals
    // Cosmetics
    33: 71.98, // NCM 33.xx - Perfumery and cosmetics
    // Auto parts
    87: 40, // NCM 87.xx - Auto parts
    // Electronics
    85: 35, // NCM 85.xx - Electronics
  };

  // Interstate adjustment factor (simplified)
  // When origin and destination are different states, MVA may be adjusted
  const isInterstate = originUf && destinationUf && originUf !== destinationUf;
  const interstateAdjustment = isInterstate ? 1.1 : 1; // 10% increase for interstate

  // Get category from first 2 digits of NCM
  if (ncm) {
    const category = parseInt(String(ncm).substring(0, 2), 10);
    const baseMva = DEFAULT_MVA_BY_CATEGORY[category];
    if (baseMva) {
      return roundCurrency(baseMva * interstateAdjustment);
    }
  }

  // Default MVA if not found (configurable at company/product level)
  return 0;
}

/**
 * Calculate complete ICMS with ST for an invoice item
 * Combines own ICMS and ICMS-ST calculations
 *
 */
function calculateIcmsWithSt({
  productValue,
  freightValue = 0,
  insuranceValue = 0,
  otherCharges = 0,
  ipiValue = 0,
  fiscalConfig = {},
  productFiscal = {},
  originUf,
  destinationUf,
}) {
  const config = fiscalConfig || {};
  const product = productFiscal || {};

  // Determine tax regime
  const taxRegime = config.taxRegime || TAX_REGIME.SIMPLES_NACIONAL;
  const isSimples =
    typeof taxRegime === 'string'
      ? taxRegime.includes('simples')
      : taxRegime === TAX_REGIME.SIMPLES_NACIONAL ||
        taxRegime === TAX_REGIME.SIMPLES_NACIONAL_EXCESS ||
        taxRegime === TAX_REGIME.SIMPLES_NACIONAL_MEI;

  // Get CST/CSOSN code
  const cstCode = isSimples
    ? (product.icmsCsosn ?? config.defaultIcmsCsosn ?? ICMS_CSOSN.TAXED_NO_CREDIT)
    : (product.icmsCst ?? config.defaultIcmsCst ?? ICMS_CST.FULLY_TAXED);

  // Calculate own ICMS first
  const ownIcmsRate = product.icmsRate ?? config.defaultIcmsRate ?? 0;
  const ownIcmsResult = calculateIcms({
    baseValue: productValue,
    rate: ownIcmsRate,
    reductionRate: product.icmsReductionRate ?? 0,
    cst: isSimples ? null : cstCode,
    csosn: isSimples ? cstCode : null,
    origin: product.icmsOrigin ?? ICMS_ORIGIN.NATIONAL,
  });

  // Check if this code requires ST calculation
  const requiresSt = isStCode(cstCode, isSimples);

  if (!requiresSt) {
    // No ST - return only own ICMS
    return {
      ...ownIcmsResult,
      hasSt: false,
    };
  }

  // Calculate ST
  const mvaRate =
    product.mvaRate ??
    getMvaRate({
      ncm: product.ncm,
      cest: product.cest,
      originUf,
      destinationUf,
    });

  // Calculate ST base
  const stBase = calculateIcmsStBase({
    productValue,
    freightValue,
    insuranceValue,
    otherCharges,
    ipiValue,
    mvaRate,
  });

  // Get internal rate (destination state rate)
  // In production, this should come from a state-by-state table
  const internalRate = product.stInternalRate ?? config.defaultStInternalRate ?? 18;
  const fcpRate = product.fcpRate ?? config.defaultFcpRate ?? 0;

  // Calculate ST value
  const stResult = calculateIcmsSt({
    stBaseValue: stBase,
    internalRate,
    ownIcmsValue: ownIcmsResult.value,
    fcpRate,
  });

  return {
    // Own ICMS
    origin: ownIcmsResult.origin,
    cst: ownIcmsResult.cst,
    csosn: ownIcmsResult.csosn,
    baseValue: ownIcmsResult.baseValue,
    rate: ownIcmsResult.rate,
    reductionRate: ownIcmsResult.reductionRate,
    value: ownIcmsResult.value,
    // ST data
    hasSt: true,
    mvaRate,
    st: {
      baseValue: stResult.stBaseValue,
      rate: stResult.stRate,
      value: stResult.stValue,
      fcpRate: stResult.fcpRate,
      fcpValue: stResult.fcpValue,
      totalValue: stResult.stTotalValue,
    },
  };
}

/**
 * Calculate all taxes for an invoice item
 */
function calculateItemTaxes({
  totalValue,
  fiscalConfig,
  productFiscal = {},
  documentType = 'nfe',
  freightValue = 0,
  insuranceValue = 0,
  otherCharges = 0,
  originUf,
  destinationUf,
}) {
  const baseValue = roundCurrency(totalValue || 0);
  const config = fiscalConfig || {};
  const product = productFiscal || {};
  const isNfce = documentType === 'nfce';
  const isNfe = documentType === 'nfe';

  // Determine tax regime - handle both string ('simples_nacional') and number (1, 2, 3, 4) formats
  const taxRegime = config.taxRegime || TAX_REGIME.SIMPLES_NACIONAL;
  const isSimples =
    typeof taxRegime === 'string'
      ? taxRegime.includes('simples')
      : taxRegime === TAX_REGIME.SIMPLES_NACIONAL ||
        taxRegime === TAX_REGIME.SIMPLES_NACIONAL_EXCESS ||
        taxRegime === TAX_REGIME.SIMPLES_NACIONAL_MEI;

  // Get rates (product overrides company defaults)
  const icmsRate = product.icmsRate ?? config.defaultIcmsRate ?? 0;
  const pisRate = product.pisRate ?? config.defaultPisRate ?? (isSimples ? 0 : 0.65);
  const cofinsRate = product.cofinsRate ?? config.defaultCofinsRate ?? (isSimples ? 0 : 3);
  const ipiRate = product.ipiRate ?? config.defaultIpiRate ?? 0;

  // Get CST/CSOSN codes
  const icmsCst = product.icmsCst ?? config.defaultIcmsCst ?? ICMS_CST.FULLY_TAXED;
  const icmsCsosn = product.icmsCsosn ?? config.defaultIcmsCsosn ?? ICMS_CSOSN.TAXED_NO_CREDIT;
  const pisCst = product.pisCst ?? config.defaultPisCst ?? PIS_COFINS_CST.OTHER_EXIT;
  const cofinsCst = product.cofinsCst ?? config.defaultCofinsCst ?? PIS_COFINS_CST.OTHER_EXIT;

  // Calculate IPI first (needed for ST base calculation)
  let ipi = null;
  let ipiValue = 0;
  if (!isNfce) {
    ipi = calculateIpi({
      baseValue,
      rate: ipiRate,
      cst: product.ipiCst ?? '50',
    });
    ipiValue = ipi.value;
  }

  // Check if this item requires ICMS-ST calculation (NFe only)
  const cstCode = isSimples ? icmsCsosn : icmsCst;
  const requiresSt = isNfe && isStCode(cstCode, isSimples) && (product.hasSt || product.mvaRate);

  let icms;

  if (requiresSt) {
    // Use the full ICMS with ST calculation
    icms = calculateIcmsWithSt({
      productValue: baseValue,
      freightValue,
      insuranceValue,
      otherCharges,
      ipiValue,
      fiscalConfig: config,
      productFiscal: product,
      originUf,
      destinationUf,
    });
  } else {
    // Standard ICMS calculation (no ST)
    const icmsResult = calculateIcms({
      baseValue,
      rate: icmsRate,
      reductionRate: product.icmsReductionRate ?? 0,
      cst: isSimples ? null : icmsCst,
      csosn: isSimples ? icmsCsosn : null,
      origin: product.icmsOrigin ?? ICMS_ORIGIN.NATIONAL,
    });

    // Build clean ICMS object - only include csosn OR cst, not both
    icms = {
      origin: icmsResult.origin,
      baseValue: icmsResult.baseValue,
      rate: icmsResult.rate,
      value: icmsResult.value,
      hasSt: false,
    };

    // Only include the appropriate tax code (csosn for Simples, cst for Regime Normal)
    if (isSimples) {
      icms.csosn = icmsResult.csosn;
    } else {
      icms.cst = icmsResult.cst;
    }
  }

  const pis = calculatePis({
    baseValue,
    rate: pisRate,
    cst: pisCst,
  });

  const cofins = calculateCofins({
    baseValue,
    rate: cofinsRate,
    cst: cofinsCst,
  });

  // Build result
  const result = {
    icms,
    pis,
    cofins,
    // Total taxes for this item
    totalTaxes: roundCurrency(icms.value + pis.value + cofins.value),
  };

  // Add ST values to total if present
  if (icms.hasSt && icms.st) {
    result.totalTaxes = roundCurrency(result.totalTaxes + icms.st.totalValue);
  }

  // Only include IPI for NFe (not for NFC-e)
  if (!isNfce && ipi) {
    result.ipi = ipi;
    result.totalTaxes = roundCurrency(result.totalTaxes + ipi.value);
  }

  return result;
}

/**
 * Calculate total taxes for an invoice from items
 */
function calculateInvoiceTaxes(items = []) {
  const totals = {
    // Own ICMS
    icmsBase: 0,
    icmsValue: 0,
    // ICMS-ST (Tax Substitution)
    icmsStBase: 0,
    icmsStValue: 0,
    fcpValue: 0,
    // Other taxes
    pisValue: 0,
    cofinsValue: 0,
    ipiValue: 0,
    // Totals
    totalTaxes: 0,
    totalStTaxes: 0,
  };

  items.forEach(item => {
    const taxes = item.taxes || {};

    // Own ICMS
    // CSOSN items (Simples Nacional) have no vBC or vICMS in SEFAZ XML - exclude from both sums
    if (!taxes.icms?.csosn) {
      totals.icmsBase += taxes.icms?.baseValue || 0;
      totals.icmsValue += taxes.icms?.value || 0;
    }

    // ICMS-ST (if present)
    if (taxes.icms?.hasSt && taxes.icms?.st) {
      totals.icmsStBase += taxes.icms.st.baseValue || 0;
      totals.icmsStValue += taxes.icms.st.value || 0;
      totals.fcpValue += taxes.icms.st.fcpValue || 0;
      totals.totalStTaxes += taxes.icms.st.totalValue || 0;
    }

    // Other taxes
    totals.pisValue += taxes.pis?.value || 0;
    totals.cofinsValue += taxes.cofins?.value || 0;
    totals.ipiValue += taxes.ipi?.value || 0;
  });

  // Round all totals
  totals.icmsBase = roundCurrency(totals.icmsBase);
  totals.icmsValue = roundCurrency(totals.icmsValue);
  totals.icmsStBase = roundCurrency(totals.icmsStBase);
  totals.icmsStValue = roundCurrency(totals.icmsStValue);
  totals.fcpValue = roundCurrency(totals.fcpValue);
  totals.totalStTaxes = roundCurrency(totals.totalStTaxes);
  totals.pisValue = roundCurrency(totals.pisValue);
  totals.cofinsValue = roundCurrency(totals.cofinsValue);
  totals.ipiValue = roundCurrency(totals.ipiValue);

  // Total taxes = own taxes + ST taxes
  totals.totalTaxes = roundCurrency(
    totals.icmsValue + totals.pisValue + totals.cofinsValue + totals.ipiValue + totals.totalStTaxes
  );

  return totals;
}

// *******************************************************************************
// ZERO-VALUE ITEM VALIDATION
// *******************************************************************************

/**
 * Check if a CFOP allows zero-value items
 */
function isCfopAllowedForZeroValue(cfop) {
  if (!cfop) return false;
  return CFOP_ZERO_VALUE_ALLOWED.includes(String(cfop));
}

/**
 * Validate if an item can have zero value based on document type and CFOP
 *
 * Rules:
 * - NFC-e: NEVER allows zero value items (SEFAZ rejects)
 * - NF-e with sale CFOP: NOT allowed (regular sale must have value)
 * - NF-e with bonification/gift CFOP: ALLOWED (5910, 5911, etc.)
 * - NFS-e: Depends on municipality, show warning
 *
 */
function validateItemZeroValue({ documentType, totalValue, cfop }) {
  const isZeroValue = !totalValue || totalValue <= 0;

  // If value is not zero, always valid
  if (!isZeroValue) {
    return { isValid: true, error: null, warning: null, type: null };
  }

  const docType = documentType?.toLowerCase();

  // NFC-e: NEVER allows zero value
  if (docType === 'nfce') {
    return {
      isValid: false,
      error: 'nfce_zero_value_not_allowed',
      warning: null,
      type: 'error',
    };
  }

  // NF-e: Depends on CFOP
  if (docType === 'nfe') {
    const cfopAllowsZero = isCfopAllowedForZeroValue(cfop);

    if (cfopAllowsZero) {
      // CFOP allows zero value (bonification, gift, sample, etc.)
      return {
        isValid: true,
        error: null,
        warning: 'nfe_zero_value_with_cfop',
        type: 'warning',
      };
    }

    // CFOP is for regular sale - zero value not allowed
    return {
      isValid: false,
      error: 'nfe_zero_value_requires_cfop',
      warning: null,
      type: 'error',
    };
  }

  // NFS-e: Warning only (depends on municipality)
  if (docType === 'nfse') {
    return {
      isValid: true, // Allow but warn
      error: null,
      warning: 'nfse_zero_value_warning',
      type: 'warning',
    };
  }

  // Unknown document type - allow with warning
  return {
    isValid: true,
    error: null,
    warning: null,
    type: null,
  };
}

/**
 * Get payment type by code
 */
function getPaymentTypeByCode(code) {
  const entry = Object.entries(PAYMENT_TYPES).find(([, v]) => v.code === code);
  if (!entry) return null;
  return {
    type: entry[0],
    code: entry[1].code,
    labelKey: entry[1].labelKey,
  };
}

/**
 * Get document status badge color
 * Aligned with backend: src/modules/domain/invoices/constants/invoiceStatus.js
 */
function getStatusColor(status, variant) {
  const isSolid = variant === 'solid';

  // For 'solid' variant, 'default' becomes almost black, so we use custom colors
  if (isSolid) {
    const solidColors = {
      [DOCUMENT_STATUS.DRAFT]: '#eaeaea', // light gray bg
      [DOCUMENT_STATUS.PROCESSING]: 'processing',
      [DOCUMENT_STATUS.AUTHORIZED]: 'success',
      [DOCUMENT_STATUS.CORRECTED]: 'warning',
      [DOCUMENT_STATUS.REJECTED]: 'error',
      [DOCUMENT_STATUS.DENIED]: 'error',
      [DOCUMENT_STATUS.ERROR]: 'orange',
      [DOCUMENT_STATUS.CANCELED]: '#8c8c8c',
      [DOCUMENT_STATUS.VOIDED]: '#8c8c8c',
    };
    return solidColors[status] || '#8c8c8c';
  }

  // Default colors
  const colors = {
    [DOCUMENT_STATUS.DRAFT]: 'default',
    [DOCUMENT_STATUS.PROCESSING]: 'processing',
    [DOCUMENT_STATUS.AUTHORIZED]: 'success',
    [DOCUMENT_STATUS.CORRECTED]: 'warning',
    [DOCUMENT_STATUS.REJECTED]: 'error',
    [DOCUMENT_STATUS.DENIED]: 'error',
    [DOCUMENT_STATUS.ERROR]: 'orange',
    [DOCUMENT_STATUS.CANCELED]: 'default',
    [DOCUMENT_STATUS.VOIDED]: 'default',
  };

  return colors[status] || 'default';
}

/**
 * Get document status badge style (for font color customization)
 * Use this together with getStatusColor when variant='solid' needs custom font color
 */
function getStatusStyle(status, variant) {
  // Only DRAFT with solid variant needs custom font color (dark gray on light bg)
  if (variant === 'solid' && status === DOCUMENT_STATUS.DRAFT) {
    return { color: '#595959' };
  }
  return undefined;
}

/**
 * Get document type label
 */
function getDocumentTypeLabel(type) {
  const labels = {
    [DOCUMENT_TYPES.NFE]: 'NF-e',
    [DOCUMENT_TYPES.NFCE]: 'NFC-e',
    [DOCUMENT_TYPES.NFSE]: 'NFS-e',
  };
  return labels[type] || type?.toUpperCase() || '';
}

/**
 * Status that are locked (document cannot be modified - already processed by SEFAZ)
 * These are final states that had approval/denial from SEFAZ
 */
const LOCKED_STATUSES = [
  DOCUMENT_STATUS.AUTHORIZED,
  DOCUMENT_STATUS.CORRECTED,
  DOCUMENT_STATUS.DENIED,
  DOCUMENT_STATUS.CANCELED,
  DOCUMENT_STATUS.VOIDED,
];

/**
 * Check if document status is locked (cannot be edited)
 * Documents are locked after SEFAZ processing (authorized, denied, canceled, voided, corrected)
 */
function isLockedStatus(status) {
  return LOCKED_STATUSES.includes(status);
}

// *******************************************************************************
// API METHODS
// *******************************************************************************

/**
 * Register company with fiscal provider
 */
async function registerCompany({ entityId, provider }) {
  const [response] = await axiosService.post({
    url: API.FISCAL.SETUP_REGISTER,
    data: { entityId, provider },
    returnAsObject: true,
  });

  return response;
}

/**
 * Validate company data before registration
 */
async function validateCompanyForRegistration({ entityId }) {
  const [response] = await axiosService.post({
    url: API.FISCAL.SETUP_VALIDATE,
    data: { entityId },
    returnAsObject: true,
  });

  return response;
}

/**
 * Get provider status for entity
 */
async function getProviderStatus(entityId) {
  const url = API.FISCAL.SETUP_STATUS.replace(':entityId', entityId);

  const [response] = await axiosService.get({
    url,
    returnAsObject: true,
  });

  return response;
}

/**
 * Validate fiscal service activation requirements (pre-validation before save)
 */
async function validateServiceActivation({ entityId, service, fiscalData }) {
  const [response] = await axiosService.post({
    url: API.FISCAL.SERVICE_VALIDATE,
    data: { entityId, service, fiscalData },
    returnAsObject: true,
  });

  return response;
}

function getIeLinks(uf, action) {
  const IE_CONSULTATION_LINKS = {
    // SOUTH REGION
    PR: {
      name: 'Paraná', // verify-ignore
      url: 'http://www.sintegra.fazenda.pr.gov.br/sintegra/',
    },
    SC: {
      name: 'Santa Catarina',
      url: 'https://sat.sef.sc.gov.br/tax.NET/tax.net.cadastro/conssc_sitcad.aspx',
    },
    RS: {
      name: 'Rio Grande do Sul',
      url: 'https://www.sefaz.rs.gov.br/consultas/contribuinte',
    },

    // SOUTHEAST REGION
    SP: {
      name: 'São Paulo', // verify-ignore
      url: 'https://www.cadesp.fazenda.sp.gov.br/Pages/Cadastro/Consultas/ConsultaPublica/ConsultaPublica.aspx',
    },
    RJ: {
      name: 'Rio de Janeiro',
      url: 'https://sucief-sincad-web.fazenda.rj.gov.br/sincad-web/index.jsf',
    },
    MG: {
      name: 'Minas Gerais',
      url: 'https://dfe-portal.svrs.rs.gov.br/Nfe/Ccc',
    },
    ES: {
      name: 'Espírito Santo', // verify-ignore
      url: 'http://www.sintegra.es.gov.br/',
    },

    // MIDWEST REGION
    DF: {
      name: 'Distrito Federal',
      url: 'http://agnet.fazenda.df.gov.br/area.cfm?id_area=1140',
    },
    GO: {
      name: 'Goiás', // verify-ignore
      url: 'http://appasp.sefaz.go.gov.br/Sintegra/Consulta/default.html',
    },
    MS: {
      name: 'Mato Grosso do Sul',
      url: 'https://cpe.sefaz.ms.gov.br/',
    },
    MT: {
      name: 'Mato Grosso',
      url: 'https://www.sefaz.mt.gov.br/cadastro/emissaocartao/emissaocartaocontribuinteacessodireto',
    },

    // NORTHEAST REGION
    BA: {
      name: 'Bahia',
      url: 'https://portal.sefaz.ba.gov.br/scripts/cadastro/cadastroBa/consultaBa.asp',
    },
    SE: {
      name: 'Sergipe',
      url: 'https://security.sefaz.se.gov.br/SIC/sintegra/index.jsp',
    },
    AL: {
      name: 'Alagoas',
      url: 'https://sintegra.sefaz.al.gov.br/#/',
    },
    PE: {
      name: 'Pernambuco',
      url: 'https://efisco.sefaz.pe.gov.br/sfi_trb_gcc/PRConsultarExtratoCadastroContribuinteSINTEGRA',
    },
    PB: {
      name: 'Paraíba', // verify-ignore
      url: 'https://www4.sefaz.pb.gov.br/sintegra/SINf_ConsultaSintegra.jsp',
    },
    RN: {
      name: 'Rio Grande do Norte',
      url: 'https://uvt2.set.rn.gov.br/#/services/consultaContribuinte',
    },
    CE: {
      name: 'Ceará', // verify-ignore
      url: 'http://internet-consultapublica.apps.sefaz.ce.gov.br/sintegra/preparar-consultar',
    },
    PI: {
      name: 'Piauí', // verify-ignore
      url: 'https://dfe-portal.svrs.rs.gov.br/Nfe/Ccc',
    },
    MA: {
      name: 'Maranhão', // verify-ignore
      url: 'https://sistemas1.sefaz.ma.gov.br/sintegra/jsp/consultaSintegra/consultaSintegraFiltro.jsf',
    },

    // NORTH REGION
    AM: {
      name: 'Amazonas',
      url: 'http://online.sefaz.am.gov.br/sintegra/index.asp',
    },
    AC: {
      name: 'Acre',
      url: 'http://sefaznet.ac.gov.br/sefazonline/servlet/hpfsincon',
    },
    RR: {
      name: 'Roraima',
      url: 'https://portalweb.sefaz.rr.gov.br/sintegra/servlet/wp_siate_consultasintegra',
    },
    PA: {
      name: 'Pará', // verify-ignore
      url: 'https://app.sefa.pa.gov.br/consulta-fic/',
    },
    AP: {
      name: 'Amapá', // verify-ignore
      url: 'https://www.sefaz.ap.gov.br/sate/seg/SEGf_AcessarFuncao.jsp?cdFuncao=CAD_011',
    },
    TO: {
      name: 'Tocantins',
      url: 'http://sintegra.sefaz.to.gov.br/sintegra/servlet/wpsico01',
    },
    RO: {
      name: 'Rondônia', // verify-ignore
      url: 'https://portalcontribuinte.sefin.ro.gov.br/Publico/parametropublica.jsp',
    },
  };

  if (uf && action === 'link') {
    return IE_CONSULTATION_LINKS[uf?.toUpperCase()] || null;
  } else if (uf && action === 'name') {
    return IE_CONSULTATION_LINKS[uf?.toUpperCase()]?.name || null;
  } else if (uf && action === 'url') {
    return IE_CONSULTATION_LINKS[uf?.toUpperCase()]?.url || null;
  } else if (uf && action === 'open') {
    window.open(IE_CONSULTATION_LINKS[uf?.toUpperCase()], '_blank', 'noopener,noreferrer');
  } else {
    return IE_CONSULTATION_LINKS;
  }
}

function getCscLinks(uf, action) {
  const CSC_LINKS = {
    // SOUTH REGION
    PR: {
      name: 'Paraná', // verify-ignore
      url: 'https://receita.pr.gov.br/login',
      note: 'Menu: DF-e > NFC-e > CSC / Controle. Requer certificado digital.',
    },
    SC: {
      name: 'Santa Catarina',
      url: 'https://sat.sef.sc.gov.br/tax.net/tax.NET.NFCe.CSC/',
      note: 'Security Code (CSC) management. Requires TTD for NFC-e and digital certificate.',
    },
    RS: {
      name: 'Rio Grande do Sul',
      url: 'https://www.sefaz.rs.gov.br/Site/MontaMenu.aspx?MenuAlias=m_nfe_nfce',
      note: 'Menu: CSC Maintenance > My Services > NFC-e. Requires digital certificate.',
    },

    // SOUTHEAST REGION
    SP: {
      name: 'São Paulo', // verify-ignore
      url: 'http://www.nfce.fazenda.sp.gov.br/NFCePortal/',
      note: 'Menu: Manage Security Code. Production only after SAT activation.',
    },
    RJ: {
      name: 'Rio de Janeiro',
      url: 'http://www4.fazenda.rj.gov.br/sefaz-dfe-csc/paginas/identificacao.jsf',
      note: 'Acesso direto com certificado digital.',
    },
    MG: {
      name: 'Minas Gerais',
      url: 'http://www.sped.fazenda.mg.gov.br/spedmg/nfce/credenciamento/',
      note: 'Seguir manual de credenciamento. Requer cadastro no SIARE.',
    },
    ES: {
      name: 'Espírito Santo', // verify-ignore
      url: 'https://e-dfe.sefaz.es.gov.br/',
      note: 'Mandatory registration before generating CSC.',
    },

    // MIDWEST REGION
    DF: {
      name: 'Distrito Federal',
      url: 'https://ww2.receita.fazenda.df.gov.br/',
      note: 'Restricted Access > Service Panel > DF-e > Security Code.',
    },
    GO: {
      name: 'Goiás', // verify-ignore
      url: 'http://nfe.sefaz.go.gov.br/nfeweb/jsp/SelecionarAmbienteCSC.jsf',
      note: 'Primeiro fazer credenciamento NF-e, depois gerar CSC.',
    },
    MS: {
      name: 'Mato Grosso do Sul',
      url: 'http://www.dfe.ms.gov.br/csc/',
      note: 'Portal direto para CSC. Credenciamento em: dfe.ms.gov.br/nfce-credenciamento/',
    },
    MT: {
      name: 'Mato Grosso',
      url: 'https://www.sefaz.mt.gov.br/nfce/codigosegurancactbn/ViewCadastroPublicoCSC.jsp',
      note: 'No prior registration required. Direct access.',
    },

    // NORTHEAST REGION
    BA: {
      name: 'Bahia',
      url: 'http://www.sefaz.ba.gov.br/especiais/como_se_tornar_emissor_nfce.html',
      note: 'No registration required. Option: Request/Void CSC.',
    },
    SE: {
      name: 'Sergipe',
      url: 'http://www.nfce.se.gov.br/portal/index.jsp',
      note: 'Production: official site. Homologation: hom.nfe.se.gov.br',
    },
    AL: {
      name: 'Alagoas',
      url: 'http://www.sefaz.al.gov.br/nfce/nfce-credenciamento',
      note: 'CSC requested IN PERSON at the Registration Office after registration.',
    },
    PE: {
      name: 'Pernambuco',
      url: 'https://efisco.sefaz.pe.gov.br/',
      note: 'e-Fisco > Tax > DFE > Register CSC. Requires digital certificate.',
    },
    PB: {
      name: 'Paraíba', // verify-ignore
      url: 'https://www.sefaz.pb.gov.br/servirtual/documentos-fiscais/nfc-e/gerar-csc',
      note: 'Portal SERvirtual. Requer login ou certificado digital.',
    },
    RN: {
      name: 'Rio Grande do Norte',
      url: 'https://uvt2.set.rn.gov.br/',
      note: 'Restricted Area > My Services > Generate CSC. Mandatory registration.',
    },
    CE: {
      name: 'Ceará', // verify-ignore
      url: 'https://www.sefaz.ce.gov.br/servicos/nfc-e/',
      note: 'After approved registration, CSC auto-generated. Check CSC on portal.',
    },
    PI: {
      name: 'Piauí', // verify-ignore
      url: 'https://siatnet.sefaz.pi.gov.br/',
      note: 'SIATWEB: Self-service > NFC-e > CSC Maintenance. Or email: nfce@sefaz.pi.gov.br',
    },
    MA: {
      name: 'Maranhão', // verify-ignore
      url: 'https://sistemas.sefaz.ma.gov.br/sefaznet/login.do?method=prepareLogin',
      note: 'SEFAZNET: automatic registration + CSC generation.',
    },

    // NORTH REGION
    AM: {
      name: 'Amazonas',
      url: 'https://online.sefaz.am.gov.br/dte/',
      note: 'No registration required. DT-e > Request CSC.',
    },
    AC: {
      name: 'Acre',
      url: 'http://sefaznet.ac.gov.br/sefazonline/',
      note: 'In-person registration required. Then: NFC-e menu.',
    },
    RR: {
      name: 'Roraima',
      url: 'https://portalapp.sefaz.rr.gov.br/nfce/servlet/wp_login',
      note: 'Menu LOGIN no Portal NFC-e.',
    },
    PA: {
      name: 'Pará', // verify-ignore
      url: 'https://app.sefa.pa.gov.br/',
      note: 'Follow SEFAZ-PA CSC generation manual.',
    },
    AP: {
      name: 'Amapá', // verify-ignore
      url: 'http://www.sefaz.ap.gov.br/sate/',
      note: 'SATE: Credenciamento > Consultar Credenciamento NFC-e > Gerar CSC.',
    },
    TO: {
      name: 'Tocantins',
      url: 'http://www.sefaz.to.gov.br/nfce/login.jsf',
      note: 'Login com IE e senha do posto fiscal. Menu: Gerar/Consultar CSC.',
    },
    RO: {
      name: 'Rondônia', // verify-ignore
      url: 'https://det.sefin.ro.gov.br/',
      note: 'Portal do Contribuinte > Manipular CSC. Login ou certificado.',
    },
  };

  if (uf && action === 'link') {
    return CSC_LINKS[uf?.toUpperCase()] || null;
  } else if (uf && action === 'name') {
    return CSC_LINKS[uf?.toUpperCase()]?.name || null;
  } else if (uf && action === 'url') {
    return CSC_LINKS[uf?.toUpperCase()]?.url || null;
  } else if (uf && action === 'note') {
    return CSC_LINKS[uf?.toUpperCase()]?.note || null;
  } else if (uf && action === 'open') {
    window.open(CSC_LINKS[uf?.toUpperCase()]?.url, '_blank', 'noopener,noreferrer');
  } else {
    return CSC_LINKS;
  }
}

// *******************************************************************************
// FISCAL FIELD CHANGE DETECTION
// *******************************************************************************

/**
 * Check if company-level fiscal fields have changed (taxRegime, IE, IBGE)
 * These fields affect ALL fiscal services (NFCe, NFe, NFSe)
 */
function hasCompanyFieldsChanged(currentFiscalData = {}, newFiscalData = {}) {
  const changedFields = [];

  // Tax Regime (CRT)
  if (currentFiscalData?.taxRegime !== newFiscalData?.taxRegime) {
    changedFields.push('taxRegime');
  }

  // State Registration (IE)
  if (currentFiscalData?.stateRegistration !== newFiscalData?.stateRegistration) {
    changedFields.push('stateRegistration');
  }

  // IBGE Code (from address - cityCode)
  if (currentFiscalData?.cityCode !== newFiscalData?.cityCode) {
    changedFields.push('cityCode');
  }

  return { changed: changedFields.length > 0, fields: changedFields };
}

/**
 * Check if NFC-e related fields have changed
 * Fields: csc.id, csc.tokenMasked, environment, serie
 * Note: We compare tokenMasked instead of token because the form doesn't have
 * the plain token for security reasons - only the masked version is displayed
 */
function hasNfceFieldsChanged(current = {}, next = {}) {
  const changedFields = [];

  // CSC ID
  if (current?.csc?.id !== next?.csc?.id) {
    changedFields.push('csc.id');
  }

  // CSC Token - only consider changed if:
  // 1. Both have plain tokens AND they differ (user is changing the token)
  // 2. Neither has a plain token but tokenMasked differs (should not happen normally)
  // NOTE: If current has no token (backend encrypted it) but next has token (form retained it),
  // this is NOT a change - it's just the form retaining the previously entered value
  const currentHasPlainToken = !!current?.csc?.token;
  const nextHasPlainToken = !!next?.csc?.token;

  if (currentHasPlainToken && nextHasPlainToken && current.csc.token !== next.csc.token) {
    // Both have tokens and they differ - user is changing to a new token
    changedFields.push('csc.token');
  } else if (!currentHasPlainToken && !nextHasPlainToken) {
    // Neither has plain token - compare masked versions
    if (current?.csc?.tokenMasked !== next?.csc?.tokenMasked) {
      changedFields.push('csc.tokenMasked');
    }
  }
  // If current has no token but next has one, it's just the form retaining the value - NOT a change

  // Environment (production/homologation)
  if (current?.environment !== next?.environment) {
    changedFields.push('environment');
  }

  // Serie
  if (current?.serie !== next?.serie) {
    changedFields.push('serie');
  }

  return { changed: changedFields.length > 0, fields: changedFields };
}

/**
 * Check if NF-e related fields have changed
 * Fields: environment, serie
 */
function hasNfeFieldsChanged(current = {}, next = {}) {
  const changedFields = [];

  // Environment (production/homologation)
  if (current?.environment !== next?.environment) {
    changedFields.push('environment');
  }

  // Serie
  if (current?.serie !== next?.serie) {
    changedFields.push('serie');
  }

  return { changed: changedFields.length > 0, fields: changedFields };
}

/**
 * Check if NFS-e related fields have changed
 * Fields: environment, municipalCode, rps.serie, rps.number
 */
function hasNfseFieldsChanged(current = {}, next = {}) {
  const changedFields = [];

  // Environment (production/homologation)
  if (current?.environment !== next?.environment) {
    changedFields.push('environment');
  }

  // Municipal code
  if (current?.municipalCode !== next?.municipalCode) {
    changedFields.push('municipalCode');
  }

  // RPS fields
  if (current?.rps?.serie !== next?.rps?.serie) {
    changedFields.push('rps.serie');
  }
  if (current?.rps?.number !== next?.rps?.number) {
    changedFields.push('rps.number');
  }

  return { changed: changedFields.length > 0, fields: changedFields };
}

/**
 * Check if NFS-e specific company fields have changed (IM - Municipal Registration)
 */
function hasNfseCompanyFieldsChanged(currentFiscalData = {}, newFiscalData = {}) {
  const changedFields = [];

  // Municipal Registration (IM) - only affects NFSe
  if (currentFiscalData?.municipalRegistration !== newFiscalData?.municipalRegistration) {
    changedFields.push('municipalRegistration');
  }

  return { changed: changedFields.length > 0, fields: changedFields };
}

/**
 * Get list of services that need validation based on current vs new fiscal data
 *
 * Validation triggers:
 * - NFCe: Being activated (false→true) OR already enabled + (card fields OR company fields changed)
 * - NFe: Being activated (false→true) OR already enabled + (card fields OR company fields changed)
 * - NFSe: Being activated (false→true) OR already enabled + (card fields OR company fields OR IM changed)
 *
 * Company fields that affect all services: taxRegime, stateRegistration (IE), cityCode (IBGE)
 * NFSe also checks: municipalRegistration (IM)
 *
 */
function getServicesToValidate(currentFiscalData = {}, newFiscalData = {}) {
  const servicesToValidate = [];

  // Check company-level field changes (affects all services)
  const companyChanges = hasCompanyFieldsChanged(currentFiscalData, newFiscalData);

  // NFCe Detection
  const nfceCurrentlyEnabled = currentFiscalData.nfce?.isEnabled === true;
  const nfceWillBeEnabled = newFiscalData.nfce?.isEnabled === true;
  const nfceBeingActivated = !nfceCurrentlyEnabled && nfceWillBeEnabled;
  const nfceCardChanges = hasNfceFieldsChanged(currentFiscalData.nfce, newFiscalData.nfce);

  // Validate if: being activated OR (already enabled AND (card OR company fields changed))
  const nfceNeedsValidation =
    nfceBeingActivated ||
    (nfceCurrentlyEnabled &&
      nfceWillBeEnabled &&
      (nfceCardChanges.changed || companyChanges.changed));

  if (nfceNeedsValidation) {
    servicesToValidate.push('nfce');
  }

  // NFe Detection
  const nfeCurrentlyEnabled = currentFiscalData.nfe?.isEnabled === true;
  const nfeWillBeEnabled = newFiscalData.nfe?.isEnabled === true;
  const nfeBeingActivated = !nfeCurrentlyEnabled && nfeWillBeEnabled;
  const nfeCardChanges = hasNfeFieldsChanged(currentFiscalData.nfe, newFiscalData.nfe);

  // Validate if: being activated OR (already enabled AND (card OR company fields changed))
  const nfeNeedsValidation =
    nfeBeingActivated ||
    (nfeCurrentlyEnabled && nfeWillBeEnabled && (nfeCardChanges.changed || companyChanges.changed));

  if (nfeNeedsValidation) {
    servicesToValidate.push('nfe');
  }

  // NFSe Detection
  const nfseCurrentlyEnabled = currentFiscalData.nfse?.isEnabled === true;
  const nfseWillBeEnabled = newFiscalData.nfse?.isEnabled === true;
  const nfseBeingActivated = !nfseCurrentlyEnabled && nfseWillBeEnabled;
  const nfseCardChanges = hasNfseFieldsChanged(currentFiscalData.nfse, newFiscalData.nfse);
  const nfseCompanyChanges = hasNfseCompanyFieldsChanged(currentFiscalData, newFiscalData);

  // Validate if: being activated OR (already enabled AND (card OR company OR IM fields changed))
  const nfseNeedsValidation =
    nfseBeingActivated ||
    (nfseCurrentlyEnabled &&
      nfseWillBeEnabled &&
      (nfseCardChanges.changed || companyChanges.changed || nfseCompanyChanges.changed));

  if (nfseNeedsValidation) {
    servicesToValidate.push('nfse');
  }

  return servicesToValidate;
}

// *******************************************************************************
// FISCAL DATA NORMALIZATION
// *******************************************************************************

/**
 * List of fiscal fields that can be cleared via allowClear
 * and need explicit null to unset in DB
 */
const CLEARABLE_FISCAL_FIELDS = [
  'defaultIcmsCst',
  'defaultPisCst',
  'defaultCofinsCst',
  'suframa',
  'mainCnae',
  'stateRegistration',
  'municipalRegistration',
];

/**
 * Normalize fiscalData before sending to backend
 * Compares with original data and sets null for clearable fields that were removed
 */
function normalizeFiscalData(fiscalData, originalFiscalData = {}) {
  if (!fiscalData) return fiscalData;

  const normalized = { ...fiscalData };

  for (const field of CLEARABLE_FISCAL_FIELDS) {
    const existsInNew = field in fiscalData;
    const existedInOriginal = field in originalFiscalData && originalFiscalData[field] != null;
    const wasCleared = existedInOriginal && !existsInNew;

    // If field existed in original but is missing from new data, set to null
    // This tells the backend to unset the value
    if (wasCleared) {
      normalized[field] = null;
    }
  }

  // Calculate isActive based on enabled fiscal services
  // isActive = true if at least one of NFe, NFCe, or NFSe is enabled
  const nfeEnabled = fiscalData?.nfe?.isEnabled || false;
  const nfceEnabled = fiscalData?.nfce?.isEnabled || false;
  const nfseEnabled = fiscalData?.nfse?.isEnabled || false;
  normalized.isActive = nfeEnabled || nfceEnabled || nfseEnabled;

  return normalized;
}

/**
 * Get list of fiscal field paths that were cleared (existed in original but not in new)
 *
 */
function getClearedFiscalFields(currentFiscalData, originalFiscalData = {}) {
  const clearedFields = [];

  for (const field of CLEARABLE_FISCAL_FIELDS) {
    const existedInOriginal = field in originalFiscalData && originalFiscalData[field] != null;
    const existsInNew = field in currentFiscalData;
    const wasCleared = existedInOriginal && !existsInNew;

    if (wasCleared) {
      clearedFields.push(`fiscalData.${field}`);
    }
  }

  return clearedFields;
}

/**
 * Clear fiscal form fields that were set to null (cleared via allowClear)
 * After save, the form still shows the old value because ignoreResetFormOnSubmit=true
 * We need to explicitly update the form to clear the Select values
 *
 */
function clearFiscalFieldsInForm(form, currentFiscalData, originalFiscalData = {}) {
  const clearedFields = getClearedFiscalFields(currentFiscalData, originalFiscalData);

  if (!form?.setFieldsValue || !currentFiscalData || clearedFields.length === 0) {
    return clearedFields;
  }

  // Build nested object for setFieldsValue (Ant Design needs this format)
  // Use null instead of undefined (Ant Design ignores undefined)
  const fiscalDataToClear = {};
  for (const fullPath of clearedFields) {
    const fieldName = fullPath.replace('fiscalData.', '');
    fiscalDataToClear[fieldName] = null;
  }

  // Get current fiscalData values and merge with cleared fields
  const currentFiscalValues = form.getFieldValue('fiscalData') || {};
  const mergedFiscalData = { ...currentFiscalValues, ...fiscalDataToClear };

  form.setFieldsValue({ fiscalData: mergedFiscalData });

  return clearedFields;
}

// *******************************************************************************
// VALIDATION WITH UI MESSAGES
// *******************************************************************************

/**
 * Pre-validate fiscal services before saving with loading/success/error messages
 * This centralizes the validation UX flow used by CompaniesDocument and SubscriptionsDocument
 *
 * @throws {Error} - Throws error with service name if validation fails (caller should catch and handle)
 */
async function validateFiscalServicesBeforeSave({ entityId, currentFiscalData, newFiscalData }) {
  // Get services that need validation
  const servicesToValidate = getServicesToValidate(currentFiscalData, newFiscalData);

  // No services need validation - return success
  if (servicesToValidate.length === 0) {
    return true;
  }

  // Show initial loading message
  const msg = await appService.message({
    flag: 'loading',
    text: 'fiscal_validating_services',
    delay: 0, // Stay indefinitely until updated
  });

  try {
    for (let i = 0; i < servicesToValidate.length; i++) {
      const service = servicesToValidate[i];
      const serviceLabel = service.toUpperCase();

      // Update message to show current service being validated
      msg.update({
        flag: 'loading',
        text: translateX('fiscal_validating_service', { service: serviceLabel }),
        delay: 0,
      });

      const validation = await validateServiceActivation({
        entityId,
        service,
        fiscalData: newFiscalData, // Pass current form values instead of DB values
      });

      if (!validation?.isValid) {
        // Show error in message
        msg.update({
          flag: 'error',
          text: translateX('fiscal_validation_failed_service', { service: serviceLabel }),
          delay: 3,
        });

        // Build error message from validation errors
        const errorMessages = (validation?.errors || [])
          .map(e => translateX(e.i18nKey) || e.message)
          .join('\n');

        appService.notification({
          flag: 'error',
          title: translateX('fiscal_service_activation_failed', { service: serviceLabel }),
          description: errorMessages || translateX('fiscal_service_validation_failed_description'),
          duration: 0,
        });

        // Throw error to abort save - caller should catch this
        throw new Error(`${serviceLabel} activation validation failed`);
      }
    }

    // All validations passed - show success message
    msg.update({
      flag: 'success',
      text: 'fiscal_all_services_validated',
      delay: 2,
    });

    return true;
  } catch (validationError) {
    // Re-throw to let caller handle
    throw validationError;
  }
}

// *******************************************************************************
// SERVICE
// *******************************************************************************
const fiscalService = {
  // SEFAZ Constants
  PAYMENT_TYPES,
  PAYMENT_INDICATOR,
  DOCUMENT_TYPES,
  DOCUMENT_STATUS,
  INVOICE_PURPOSE,
  INVOICE_PURPOSE_CODES,
  OPERATION_TYPE,
  CONSUMER_PRESENCE,
  TAX_REGIME,
  CRT_TAG_CONFIG,
  ICMS_ORIGIN,
  ICMS_CST,
  ICMS_CSOSN,
  ICMS_CST_WITH_ST,
  ICMS_CSOSN_WITH_ST,
  PIS_COFINS_CST,
  FREIGHT_MODALITY,
  EMISSION_ENVIRONMENT,
  FINAL_CONSUMER,
  INTERMEDIATION,
  INVOICE_NATURE,
  NFE_MODEL,
  RECIPIENT_IE_INDICATOR,
  NFE_EMISSION_TYPE,

  // Tax calculation functions
  calculateIcms,
  calculatePis,
  calculateCofins,
  calculateIpi,
  calculateItemTaxes,
  calculateInvoiceTaxes,

  // ICMS-ST calculation functions (NFe only)
  calculateIcmsStBase,
  calculateIcmsSt,
  calculateIcmsWithSt,
  isStCode,
  getMvaRate,

  // Zero-value validation functions
  isCfopAllowedForZeroValue,
  validateItemZeroValue,

  // Helper functions
  getPaymentTypeByCode,
  getStatusColor,
  getStatusStyle,
  getDocumentTypeLabel,

  // API methods
  registerCompany,
  validateCompanyForRegistration,
  getProviderStatus,
  validateServiceActivation,

  // Validation with UI messages
  validateFiscalServicesBeforeSave,

  // Data normalization & form clearing
  normalizeFiscalData,
  clearFiscalFieldsInForm,
  getClearedFiscalFields,
  CLEARABLE_FISCAL_FIELDS,

  // Link helpers
  getIeLinks,
  getCscLinks,

  // Field change detection helpers
  hasCompanyFieldsChanged,
  hasNfceFieldsChanged,
  hasNfeFieldsChanged,
  hasNfseFieldsChanged,
  hasNfseCompanyFieldsChanged,
  getServicesToValidate,
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default fiscalService;

// Named exports for tree-shaking
export {
  // SEFAZ Constants
  PAYMENT_TYPES,
  PAYMENT_INDICATOR,
  DOCUMENT_TYPES,
  DOCUMENT_STATUS,
  INVOICE_PURPOSE,
  INVOICE_PURPOSE_CODES,
  OPERATION_TYPE,
  CONSUMER_PRESENCE,
  TAX_REGIME,
  CRT_TAG_CONFIG,
  ICMS_ORIGIN,
  ICMS_CST,
  ICMS_CSOSN,
  ICMS_CST_WITH_ST,
  ICMS_CSOSN_WITH_ST,
  PIS_COFINS_CST,
  FREIGHT_MODALITY,
  EMISSION_ENVIRONMENT,
  FINAL_CONSUMER,
  INTERMEDIATION,
  INVOICE_NATURE,
  NFE_MODEL,
  RECIPIENT_IE_INDICATOR,
  NFE_EMISSION_TYPE,
  // Tax calculation functions
  calculateItemTaxes,
  calculateInvoiceTaxes,
  // ICMS-ST calculation functions (NFe only)
  calculateIcmsStBase,
  calculateIcmsSt,
  calculateIcmsWithSt,
  isStCode,
  getMvaRate,
  // Zero-value validation functions
  validateItemZeroValue,
  // Helper functions
  getPaymentTypeByCode,
  getStatusColor,
  getStatusStyle,
  getDocumentTypeLabel,
  // Status check functions
  isLockedStatus,
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
